/**
 * The saved file: a score in an envelope that says what it is.
 *
 * The document is already plain JSON -- ids are strings, everything else is objects,
 * arrays, numbers and booleans -- so saving is `JSON.stringify` and the interesting part
 * is everything around it.
 *
 * The envelope carries a format name and a version. The name is there so that a file
 * dropped on the editor can be recognised or refused before it is read; the version is
 * there so that a file written by an older editor can be brought forward by
 * {@link migrate} rather than rejected. Writing the version *into* the file is the whole
 * point: a document that does not say which rules it was written under cannot be read
 * safely by anything but the exact code that wrote it.
 *
 * Nothing here throws. Opening a file the user chose is the most ordinary way for an
 * editor to meet something unexpected, and the answer belongs on the screen as a sentence.
 */

import type { Score } from "../document/types";
import { ReadFault, at, field, record, text, whole } from "./reader";
import { readScore } from "./read-score";

/** What a file of ours says it is. */
export const FORMAT = "score-editor";

/** The version this editor writes. Raise it only alongside a step in {@link migrate}. */
export const VERSION = 1;

/** A saved document. */
export interface ScoreFile {
  readonly format: typeof FORMAT;
  readonly version: number;
  /** When it was written, as an ISO 8601 string, for a caller that lists files. */
  readonly savedAt: string;
  readonly score: Score;
}

/** A file that was read, or the reason it could not be. */
export type ReadResult =
  | { readonly ok: true; readonly file: ScoreFile; readonly migrated: boolean }
  | { readonly ok: false; readonly reason: string };

/** A score as the text of a file. `pretty` is for a document kept in version control. */
export function serialize(score: Score, pretty = false): string {
  const file: ScoreFile = {
    format: FORMAT,
    version: VERSION,
    savedAt: new Date().toISOString(),
    score,
  };
  return JSON.stringify(file, null, pretty ? 2 : 0);
}

/**
 * Bring a document written by an older editor up to the current version.
 *
 * There is nothing to do yet, because there has only ever been one version. The shape is
 * here so that the first change to the document has an obvious place to go: a step per
 * version, each turning the shape one version older into the shape one version newer, run
 * in order. A file from a *newer* editor is refused rather than guessed at -- forward
 * compatibility is a promise this format does not make.
 */
function migrate(body: unknown, version: number): unknown | string {
  if (version > VERSION) return `this file was written by a newer editor (version ${version})`;
  if (version < 1) return `this file states an impossible version (${version})`;
  return body;
}

/** Read the text of a file, answering with the document or with what is wrong with it. */
export function parse(source: string): ReadResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(source) as unknown;
  } catch {
    return { ok: false, reason: "this file is not JSON" };
  }

  try {
    record(parsed, "");
    const format = at(parsed, "format", "", text);
    if (format !== FORMAT) return { ok: false, reason: `this is not a ${FORMAT} file` };
    const version = at(parsed, "version", "", whole);

    const brought = migrate(parsed, version);
    if (typeof brought === "string") return { ok: false, reason: brought };

    const savedAt = at(brought, "savedAt", "", text);
    const score = readScore(field(brought, "score", ""), "score");
    return {
      ok: true,
      file: { format: FORMAT, version: VERSION, savedAt, score },
      migrated: version !== VERSION,
    };
  } catch (error) {
    if (error instanceof ReadFault) return { ok: false, reason: error.message };
    if (error instanceof RangeError) return { ok: false, reason: error.message };
    throw error;
  }
}

/** Just the score out of a file's text, or `null` when it cannot be read. */
export function parseScore(source: string): Score | null {
  const result = parse(source);
  return result.ok ? result.file.score : null;
}
